import { useState } from 'react';
import { Modal, Stack, PasswordInput, Button, Alert, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../store/auth';

// Full-screen blocking dialog shown when the current user has must_change_password = true.
// The user cannot dismiss it; it only closes on successful password change.
export default function ForcePasswordChange() {
  const { me, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!me?.mustChangePassword) return null;

  async function submit() {
    setErr(null);
    if (!current) return setErr('Enter your current password.');
    if (next.length < 8) return setErr('New password must be at least 8 characters.');
    if (next !== confirm) return setErr('The two new-password fields do not match.');
    if (next === current) return setErr('The new password must differ from the current one.');
    setBusy(true);
    try {
      await changePassword(current, next);
      notifications.show({ message: 'Password updated.', color: 'green' });
    } catch (e: any) {
      setErr(e.message ?? 'Change failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={() => { /* cannot dismiss without changing */ }}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={<span className="modal-title-custom">Change your password</span>}
    >
      <Stack>
        <Text size="sm">
          Your password was reset by an administrator. Choose a new password before continuing.
        </Text>
        <PasswordInput
          label="Current (admin-provided) password"
          value={current}
          onChange={(e) => setCurrent(e.currentTarget.value)}
          autoFocus
        />
        <PasswordInput
          label="New password"
          description="Minimum 8 characters"
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
        />
        <PasswordInput
          label="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.currentTarget.value)}
        />
        {err && <Alert color="red">{err}</Alert>}
        <Button onClick={submit} loading={busy}>Update password</Button>
        <Button
          variant="subtle"
          color="gray"
          onClick={async () => { await logout(); window.location.href = '/login'; }}
        >
          Log out instead
        </Button>
      </Stack>
    </Modal>
  );
}
